import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Switch,
  Upload,
  Image,
} from "antd";
import {
  FileImageOutlined,
  FileOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Title from "antd/es/typography/Title";
import { getFilePath } from "../../config/app-config";
import AuthService from "../../session/AuthService";

const { Option } = Select;

const UserFormModal = ({ visible, onCancel, onSave, editingUser, form }) => {
  const [docCopy, setDocCopy] = React.useState("");
  const [addressProofCopy, setAddressProofCopy] = useState("");
  const [idProofCopy, setIdProofCopy] = useState("");
  const [otherProofCopy, setOtherProofCopy] = useState("");

  const currentUserType = AuthService.getUserData()?.uType || "";

  useEffect(() => {
    if (visible) {
      if (editingUser) {
        const kyc = editingUser?.kyc || {};
        form.setFieldsValue({
          ...editingUser,
          email: editingUser.email || "",
          dob: editingUser.dob ? dayjs(editingUser.dob) : null,
          nationality: editingUser.nationality || "",
          userStatus: editingUser.userStatus === 1,
          customerType: editingUser.customerType,
          addressProofFile: [],
          idProofFile: [],
          otherProofFile: [],
        });
        setAddressProofCopy(kyc?.addressProofFile || "");
        setIdProofCopy(kyc?.idProofFile || "");
        setOtherProofCopy(kyc?.otherProofFile || "");
      } else {
        form.resetFields();
        form.setFieldsValue({ userStatus: true });
        setAddressProofCopy("");
        setIdProofCopy("");
        setOtherProofCopy("");
      }
    }
  }, [visible, editingUser, form]);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        const formData = new FormData();

        if (editingUser?.id) {
          formData.append("id", editingUser.id);
        }

        formData.append("name", values.name);
        formData.append("email", values.email);
        formData.append("mobileNo", values.mobileNo);
        formData.append(
          "dob",
          values.dob ? values.dob.format("YYYY-MM-DD") : ""
        );
        formData.append("gender", values.gender);
        formData.append("nationality", values.nationality || "");
        formData.append("userType", values.userType);
        formData.append("userStatus", values.userStatus ? 1 : 0);
        formData.append(
          "customerType",
          values.customerType ? values.customerType : 1
        );

        if (values.addressProofFile?.length > 0) {
          formData.append(
            "addressProofFile",
            values.addressProofFile[0].originFileObj
          );
        }

        if (values.idProofFile?.length > 0) {
          formData.append("idProofFile", values.idProofFile[0].originFileObj);
        }

        if (values.otherProofFile?.length > 0) {
          formData.append(
            "otherProofFile",
            values.otherProofFile[0].originFileObj
          );
        }

        onSave(formData);
      })
      .catch((err) => {
        // Log error in detail
        console.error("Validation or save error:", err);
        if (err?.errorFields) {
          // Validation error
          console.warn("Validation failed:", err.errorFields);
        } else if (err?.message) {
          console.error("Error message:", err.message);
        } else {
          console.error("Unknown error:", JSON.stringify(err));
        }
      });
  };

  const renderUpload = (
    label,
    fieldName,
    fileState,
    setFileState,
    required
  ) => (
    <Col span={8}>
      <Form.Item
        name={fieldName}
        label={label}
        valuePropName="fileList"
        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
        rules={
          required && !fileState
            ? [{ required: true, message: `Please upload your ${label}` }]
            : []
        }
      >
        <Upload name="file" listType="text" beforeUpload={() => false}>
          <Button icon={<UploadOutlined />}>Click to Upload</Button>
        </Upload>
      </Form.Item>

      {fileState && (
        <div style={{ marginBottom: "1rem" }}>
          {/\.(jpg|jpeg|png|gif)$/i.test(fileState) ? (
            <Image
              preview={{ mask: <span>Click to preview</span> }}
              src={getFilePath(fileState)}
              alt={label}
              width={50}
              style={{ cursor: "pointer", borderRadius: 4 }}
              placeholder={
                <FileImageOutlined style={{ fontSize: 40, color: "#999" }} />
              }
            />
          ) : (
            <a
              href={getFilePath(fileState)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <FileOutlined style={{ fontSize: 40 }} />
              View File
            </a>
          )}
        </div>
      )}
    </Col>
  );

  return (
    <Modal
      title={editingUser ? "Update User" : "Add User"}
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {editingUser ? "Update User" : "Add User"}
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          {/* Name */}
          <Col span={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter name" }]}
            >
              <Input placeholder="Enter name" />
            </Form.Item>
          </Col>

          {/* Email */}
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" />
            </Form.Item>
          </Col>

          {/* Phone */}
          <Col span={12}>
            <Form.Item
              name="mobileNo"
              label="Phone"
              rules={[{ required: true, message: "Please enter phone number" }]}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>
          </Col>

          {/* DOB */}
          <Col span={12}>
            <Form.Item
              name="dob"
              label="Date of Birth"
              rules={[{ required: true }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          {/* Gender */}
          <Col span={12}>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Select placeholder="Select gender">
                <Option value="MALE">Male</Option>
                <Option value="FEMALE">Female</Option>
                <Option value="OTHER">Other</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Nationality */}
          <Col span={12}>
            <Form.Item
              name="nationality"
              label="Nationality"
              rules={[
                { required: true, message: "Please select your nationality" },
              ]}
            >
              <Select placeholder="Select your nationality">
                <Option value="Indian">Indian</Option>
                <Option value="American">American</Option>
                <Option value="Canadian">Canadian</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* User Type */}
          <Col span={12}>
            <Form.Item
              name="userType"
              label="User Type"
              rules={[{ required: true, message: "Please select user type" }]}
            >
              <Select placeholder="Select user type">
                {currentUserType === 1 && (
                  <Option value={4}>Super Agent</Option>
                )}
                {currentUserType === 4 && <Option value={5}>Agent</Option>}
                {currentUserType === 1 && <Option value={3}>Merchant</Option>}
                {currentUserType === 1 && <Option value={2}>Enterprise</Option>}
                {(currentUserType === 1 || currentUserType === 5) && (
                  <Option value={6}>Customer</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customerType"
              label="Customer Type"
              rules={[
                { required: true, message: "Please select customer type" },
              ]}
            >
              <Select placeholder="Select user type">
                <Option value={1}>Program 1</Option>
                <Option value={2}>Program 2</Option>
                <Option value={3}>Program 3</Option>
              </Select>
            </Form.Item>
          </Col>
          {/* Status */}
          <Col span={12}>
            <Form.Item name="userStatus" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Suspended" />
            </Form.Item>
          </Col>

          {/* KYC Section Title */}
          <Col span={24}>
            <Title level={5} style={{ margin: "5px 0 8px" }}>
              KYC Details
            </Title>
          </Col>

          {renderUpload(
            "Address Proof",
            "addressProofFile",
            addressProofCopy,
            setAddressProofCopy,
            true
          )}
          {renderUpload(
            "ID Proof",
            "idProofFile",
            idProofCopy,
            setIdProofCopy,
            true
          )}
          {renderUpload(
            "Other Proof",
            "otherProofFile",
            otherProofCopy,
            setOtherProofCopy,
            false
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default UserFormModal;
